/**
 * Utilidades para manejar texto enriquecido básico (HTML) de forma segura.
 *
 * El editor produce HTML simple (negrita, cursiva, subrayado y listas). Estas
 * funciones permiten:
 *  - `sanitizeHtml`: limpiar el HTML antes de renderizarlo con dangerouslySetInnerHTML.
 *  - `htmlToPlainText`: degradar el HTML a texto plano (para PDF, vistas truncadas
 *    o búsquedas).
 *  - `isHtmlEmpty`: saber si el contenido es realmente vacío (sin solo etiquetas).
 */

/** Etiquetas permitidas en el HTML enriquecido. */
const ALLOWED_TAGS = new Set([
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'UL',
  'OL',
  'LI',
  'BR',
  'P',
  'DIV',
  'SPAN',
])

/**
 * Sanitiza una cadena HTML dejando solo etiquetas de formato básicas y
 * eliminando todos los atributos (evita XSS por `on*`, `style`, `href`, etc.).
 *
 * @param {string} [html='']
 * @returns {string} HTML seguro para renderizar.
 */
export const sanitizeHtml = (html = '') => {
  if (!html || typeof document === 'undefined') return html || ''

  const root = document.createElement('div')
  root.innerHTML = html

  const clean = (node) => {
    // Copiamos la lista porque la mutamos durante el recorrido
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        // Quitar todos los atributos
        Array.from(child.attributes).forEach((attr) =>
          child.removeAttribute(attr.name)
        )

        if (!ALLOWED_TAGS.has(child.tagName)) {
          // Etiqueta no permitida → la "desenvolvemos" conservando su contenido
          while (child.firstChild) node.insertBefore(child.firstChild, child)
          node.removeChild(child)
        } else {
          clean(child)
        }
      } else if (child.nodeType !== Node.TEXT_NODE) {
        // Comentarios u otros nodos → fuera
        node.removeChild(child)
      }
    })
  }

  clean(root)
  return root.innerHTML
}

/**
 * Convierte HTML enriquecido a texto plano legible, preservando saltos de línea
 * y marcando los elementos de lista con viñetas.
 *
 * @param {string} [html='']
 * @returns {string}
 */
export const htmlToPlainText = (html = '') => {
  if (!html) return ''
  if (typeof document === 'undefined') return html

  const normalized = html
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/(p|div|ul|ol|h[1-6])>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')

  const tmp = document.createElement('div')
  tmp.innerHTML = normalized
  const text = tmp.textContent || ''

  return text
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .trim()
}

/**
 * Indica si el contenido enriquecido está vacío (sin texto real).
 *
 * @param {string} [html='']
 * @returns {boolean}
 */
export const isHtmlEmpty = (html = '') =>
  htmlToPlainText(html).trim().length === 0
